import { VEHICLE_CHECK_MODELS_API_URL } from 'constants/ApiConstants';
import * as types from 'modules/vehicleCheckModels/actionTypes';

/**
 * @param {VehicleChecksFilters} params
 * @param {Object} [meta={}]
 * @param {boolean} alterable
 * @returns {FetchVehicleCheckModelsAction}
 */
export const fetchVehicleCheckModels = (params, meta = {}, available = false) => {
  const target = available ? 'available' : 'alterable';
  return {
    type: types.FETCH_VEHICLE_CHECK_MODELS,
    payload: {
      request: {
        method: 'GET',
        url: `${VEHICLE_CHECK_MODELS_API_URL}/search/${target}`,
        params: {
          sort: 'name,asc',
          status: 'OPEN',
          projection: 'page',
          page: 0,
          size: 10,
          ...params,
        },
      },
    },
    meta,
  };
};

/**
 * @param {import('modules/global/generic.types').PageContent<import('../types').VehicleCheckModel>} page
 * @param {Object} [meta={}]
 * @returns {FetchVehicleCheckModelsSuccessAction}
 */
export const fetchVehicleCheckModelsSuccess = (page, meta = {}) => ({
  type: types.FETCH_VEHICLE_CHECK_MODELS_SUCCESS,
  payload: page,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {FetchVehicleCheckModelsFailureAction}
 */
export const fetchVehicleCheckModelsFailure = (error, meta = {}) => ({
  type: types.FETCH_VEHICLE_CHECK_MODELS_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} vehicleCheckModelId
 * @param {Object} [meta={}]
 * @returns {FetchVehicleCheckModelAction}
 */
export const fetchVehicleCheckModel = (vehicleCheckModelId, meta = {}) => ({
  type: types.FETCH_VEHICLE_CHECK_MODEL,
  payload: {
    request: {
      method: 'GET',
      url: `${VEHICLE_CHECK_MODELS_API_URL}/${vehicleCheckModelId}`,
      params: { projection: 'details' },
    },
  },
  meta,
});

/**
 * @param {import('../types').VehicleCheckModel} page
 * @param {Object} [meta={}]
 * @returns {FetchVehicleCheckModelSuccessAction}
 */
export const fetchVehicleCheckModelSuccess = (vehicleCheckModel, meta = {}) => ({
  type: types.FETCH_VEHICLE_CHECK_MODEL_SUCCESS,
  payload: vehicleCheckModel,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {FetchVehicleCheckModelFailureAction}
 */
export const fetchVehicleCheckModelFailure = (error, meta = {}) => ({
  type: types.FETCH_VEHICLE_CHECK_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {string} vehicleCheckModelId
 * @param {Object} [meta={}]
 * @returns {DeleteVehicleCheckModelAction}
 */
export const deleteVehicleCheckModel = (vehicleCheckModelId, meta = {}) => ({
  type: types.DELETE_VEHICLE_CHECK_MODEL,
  payload: {
    request: {
      method: 'DELETE',
      url: `${VEHICLE_CHECK_MODELS_API_URL}/${vehicleCheckModelId}`,
    },
    id: vehicleCheckModelId,
  },
  meta,
});

/**
 * @param {string} vehicleCheckModelId
 * @param {Object} [meta={}]
 * @returns {DeleteVehicleCheckModelSuccessAction}
 */
export const deleteVehicleCheckModelSuccess = (vehicleCheckModelId, meta = {}) => ({
  type: types.DELETE_VEHICLE_CHECK_MODEL_SUCCESS,
  payload: vehicleCheckModelId,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {DeleteVehicleCheckModelFailureAction}
 */
export const deleteVehicleCheckModelFailure = (error, meta = {}) => ({
  type: types.DELETE_VEHICLE_CHECK_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 *
 * @param {{id : string, data: { dealerId?: string, groupId?: string }}} params
 * @param {Object} [meta={}]
 * @returns {CloneVehicleCheckModelAction}
 */
export const cloneVehicleCheckModel = ({ id: vehicleCheckModelId, ...data }, meta = {}) => ({
  type: types.CLONE_VEHICLE_CHECK_MODEL,
  payload: {
    request: {
      method: 'POST',
      url: `${VEHICLE_CHECK_MODELS_API_URL}/${vehicleCheckModelId}/clone`,
      data,
    },
  },
  meta,
});

/**
 * @param {import('../types').VehicleCheckModel} vehicleCheckModel
 * @param {Object} [meta={}]
 * @returns {CloneVehicleCheckModelSuccessAction}
 */
export const cloneVehicleCheckModelSuccess = (vehicleCheckModel, meta = {}) => ({
  type: types.CLONE_VEHICLE_CHECK_MODEL_SUCCESS,
  payload: vehicleCheckModel,
  meta,
});

/**
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {CloneVehicleCheckModelFailureAction}
 */
export const cloneVehicleCheckModelFailure = (error, meta = {}) => ({
  type: types.CLONE_VEHICLE_CHECK_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {CreateVehicleCheckModelPayload} data
 * @param {Object} [meta={}]
 * @returns {CreateVehicleCheckModelAction}
 */
export const createVehicleCheckModel = (data, meta = {}) => ({
  type: types.CREATE_VEHICLE_CHECK_MODEL,
  payload: {
    request: {
      method: 'POST',
      url: VEHICLE_CHECK_MODELS_API_URL,
      data,
    },
  },
  meta,
});

/**
 * @param {import('../types').VehicleCheckModel} vehicleCheckModel
 * @param {Object} [meta={}]
 * @returns {CreateVehicleCheckModelSuccessAction}
 */
export const createVehicleCheckModelSuccess = (vehicleCheckModel, meta = {}) => ({
  type: types.CREATE_VEHICLE_CHECK_MODEL_SUCCESS,
  payload: vehicleCheckModel,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {CreateVehicleCheckModelFailureAction}
 */
export const createVehicleCheckModelFailure = (error, meta = {}) => ({
  type: types.CREATE_VEHICLE_CHECK_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});

/**
 * @param {UpdateVehicleCheckModelPayload} vehicleCheckModel
 * @param {Object} [meta={}]
 * @returns {UpdateVehicleCheckModelAction}
 */
export const updateVehicleCheckModel = (vehicleCheckModel, meta = {}) => ({
  type: types.UPDATE_VEHICLE_CHECK_MODEL,
  payload: {
    request: {
      method: 'PUT',
      url: `${VEHICLE_CHECK_MODELS_API_URL}/${vehicleCheckModel.id}`,
      data: vehicleCheckModel,
    },
  },
  meta,
});

/**
 * @param {import('../types').VehicleCheckModel} vehicleCheckModel
 * @param {Object} [meta={}]
 * @returns {UpdateVehicleCheckModelSuccessAction}
 */
export const updateVehicleCheckModelSuccess = (vehicleCheckModel, meta = {}) => ({
  type: types.UPDATE_VEHICLE_CHECK_MODEL_SUCCESS,
  payload: vehicleCheckModel,
  meta,
});

/**
 * @param {string} message
 * @param {Object} [meta={}]
 * @returns {UpdateVehicleCheckModelFailureAction}
 */
export const updateVehicleCheckModelFailure = (error, meta = {}) => ({
  type: types.UPDATE_VEHICLE_CHECK_MODEL_FAILURE,
  payload: error,
  error: true,
  meta,
});
